import React, { useState, useEffect } from "react";
import { ArrowLeft, ChevronDown, ExternalLink, Mail, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import { fetchShowcaseDeepThinkers } from "../service/ShowcaseService";

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

function AuthorCard({ author, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen || false);
    const hasMeta = author.affiliation || author.role || author.orcid || author.email;

    return (
        <div className="submissions-card" style={{ marginBottom: 12 }}>
            <button className="analysis-group-header" style={open ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}} onClick={() => setOpen(o => !o)}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{author.name}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="analysis-group-count">
                        {author.paperCount} {author.paperCount === 1 ? "paper" : "papers"}
                    </span>
                    <ChevronDown size={15} className={`analysis-chevron ${open ? "analysis-chevron-open" : ""}`} />
                </div>
            </button>

            {open && (
                <div style={{ padding: "0 18px 16px" }}>
                    {author.affiliation && (
                        <p style={{ fontSize: 12.5, color: "var(--ink-400)", margin: "8px 0 0" }}>{author.affiliation}</p>
                    )}
                    {hasMeta && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14, marginTop: 8 }}>
                            {author.role && (
                                <span className="similar-works-section-badge" style={{ background: "var(--surface-4)", color: "var(--ink-600)", border: "none", fontSize: 11.5 }}>
                                    {author.role}
                                </span>
                            )}
                            {author.orcid && (
                                <a
                                    href={`https://orcid.org/${author.orcid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="similar-works-section-badge"
                                    style={{ background: "var(--success-bg)", color: "var(--success)", border: "none", fontSize: 11.5, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                                >
                                    ORCID <ExternalLink size={10} />
                                </a>
                            )}
                            {author.email && (
                                <a
                                    href={`mailto:${author.email}`}
                                    className="similar-works-section-badge"
                                    style={{ background: "var(--info-bg)", color: "var(--link-blue)", border: "none", fontSize: 11.5, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                                >
                                    <Mail size={10} /> {author.email}
                                    {author.emailConfidence && <span style={{ opacity: 0.7 }}> ({author.emailConfidence})</span>}
                                </a>
                            )}
                        </div>
                    )}

                    {author.connectionSummary && (
                        <p style={{ fontSize: 12.5, color: "var(--ink-500)", margin: "0 0 14px", lineHeight: 1.5 }}>
                            {author.connectionSummary}
                        </p>
                    )}

                    {author.papers && author.papers.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                            {author.papers.map((p, i) => (
                                <div
                                    key={i}
                                    style={{
                                        padding: "12px 0",
                                        borderTop: i === 0 ? "1px solid var(--border-subtle)" : "none",
                                        borderBottom: i < author.papers.length - 1 ? "1px solid var(--border-subtle)" : "none",
                                        cursor: p.pmid ? "pointer" : "default",
                                    }}
                                    onClick={() => p.pmid && window.open(`https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`, "_blank")}
                                >
                                    <p style={{ margin: 0, fontWeight: 500, fontSize: 13, color: "var(--ink-900)" }}>{p.title}</p>
                                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 6 }}>
                                        {p.similarity && (
                                            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-900)" }}>{p.similarity}</span>
                                        )}
                                        {p.cluster && (
                                            <span className="similar-works-section-badge" style={{ background: "var(--surface-3)", color: "var(--ink-500)", border: "none", fontSize: 11 }}>
                                                {p.cluster.replace(/^[A-Z]\d+:\s*/, "")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ShowcaseDeepThinkers() {
    const navigate = useNavigate();
    const { key } = useParams();
    const [deepThinkers, setDeepThinkers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    useEffect(() => {
        if (!key) return;
        fetchShowcaseDeepThinkers(key)
            .then(res => setDeepThinkers(res.deepThinkers ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [key]);

    const filtered = deepThinkers.filter(dt => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (dt.name ?? "").toLowerCase().includes(q)
            || (dt.affiliation ?? "").toLowerCase().includes(q);
    });

    useEffect(() => { setPage(1); }, [search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    return (
        <PublicLayout>
            <button className="chpw-back" onClick={() => navigate(`/showcase/${key}`)}>
                <ArrowLeft size={14} />
                Back to Showcase
            </button>

            <div className="upload-page-title-row">
                <div>
                    <h1 className="upload-page-heading">DeepThinkers</h1>
                    {!loading && <p className="upload-page-meta">{deepThinkers.length} researcher{deepThinkers.length !== 1 ? "s" : ""} connected through the knowledge graph</p>}
                </div>
            </div>

            <div className="submissions-filters" style={{ marginBottom: 16 }}>
                <div className="submissions-search-wrap" style={{ position: "relative", marginLeft: "auto" }}>
                    <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", outline: "none", width: 160 }}
                    />
                    {search && (
                        <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>x</button>
                    )}
                </div>
            </div>

            {!loading && totalPages > 1 && (
                <div className="pagination-bar" style={{ marginBottom: 12 }}>
                    <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}-{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</p>
                    <div className="pagination-controls">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                        <div className="pagination-pages">
                            {pageWindows(safePage, totalPages).map((n, i) =>
                                n === null
                                    ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>...</span>
                                    : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                            )}
                        </div>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                    </div>
                    <div className="pagination-spacer" />
                </div>
            )}

            {loading ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="submissions-card" style={{ padding: "18px" }}>
                            <div className="skeleton" style={{ height: 14, width: "40%", marginBottom: 8 }} />
                            <div className="skeleton" style={{ height: 11, width: "25%" }} />
                        </div>
                    ))}
                </div>
            ) : filtered.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    {paginated.map((author, i) => (
                        <AuthorCard key={`${safePage}-${i}`} author={author} />
                    ))}
                </div>
            ) : (
                <div className="submissions-card" style={{ padding: "32px 24px", textAlign: "center" }}>
                    <p className="settings-empty">{search ? "No researchers match your search." : "No DeepThinkers data available for this submission."}</p>
                </div>
            )}
        </PublicLayout>
    );
}
